import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { onKey } from "../../lib/shortcuts/onKey.js";
import { useEnterStack, useEscapeStack, useSlashStack } from "../../lib/shortcuts/useEscapeStack.js";

function buildSelectionSnapshot(nextSelection) {
  return JSON.stringify({
    rowIndex: nextSelection?.rowIndex ?? null,
    colIndex: nextSelection?.colIndex ?? null,
    rowId: nextSelection?.row?.id ?? null,
    columnKey: nextSelection?.column?.key ?? null,
    editing: Boolean(nextSelection?.editing)
  });
}

export function useSlashActionToolbar({
  canOpen,
  actions,
  focusContextKey = "",
  gridFocused = false
}) {
  const [requestedOpen, setRequestedOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const openedContextKeyRef = useRef("");
  const isOpen = requestedOpen
    && Boolean(focusContextKey)
    && focusContextKey === openedContextKeyRef.current
    && gridFocused;

  const resetToolbarState = useCallback(() => {
    setRequestedOpen(false);
    setSelectedIndex(0);
    openedContextKeyRef.current = "";
  }, []);
  const closeToolbar = resetToolbarState;

  const cycleToolbar = useCallback(() => {
    if (actions.length <= 1 || selectedIndex >= actions.length - 1) {
      closeToolbar();
      return;
    }

    setSelectedIndex((current) => (current + 1) % actions.length);
  }, [actions.length, closeToolbar, selectedIndex]);

  const activateToolbarAction = useCallback(() => {
    const selectedAction = actions[selectedIndex];
    if (!selectedAction) {
      return;
    }

    resetToolbarState();
    selectedAction.onSelect?.();
  }, [actions, resetToolbarState, selectedIndex]);

  useLayoutEffect(() => {
    if (!canOpen && requestedOpen) {
      resetToolbarState();
    }
  }, [canOpen, requestedOpen, resetToolbarState]);
  useLayoutEffect(() => {
    if (!requestedOpen) {
      return;
    }

    if (!gridFocused || !focusContextKey || focusContextKey !== openedContextKeyRef.current) {
      resetToolbarState();
    }
  }, [focusContextKey, gridFocused, requestedOpen, resetToolbarState]);

  useEscapeStack(() => {
    if (!isOpen) {
      return false;
    }

    closeToolbar();
    return true;
  }, isOpen);
  useEnterStack(() => {
    if (!isOpen) {
      return false;
    }

    activateToolbarAction();
    return true;
  }, isOpen);
  useSlashStack(() => {
    if (!isOpen) {
      return false;
    }

    cycleToolbar();
    return true;
  }, isOpen);
  const toolbarActions = useMemo(
    () => actions.map((action, index) => ({
      ...action,
      selected: index === selectedIndex
    })),
    [actions, selectedIndex]
  );

  return {
    activateToolbarAction,
    closeToolbar,
    cycleToolbar,
    selectedIndex,
    setSelectedIndex,
    toolbarActions,
    toolbarOpen: isOpen,
    setToolbarOpen(nextOpen) {
      const resolvedOpen = typeof nextOpen === "function" ? nextOpen(requestedOpen) : nextOpen;
      if (!resolvedOpen) {
        closeToolbar();
        return;
      }

      openedContextKeyRef.current = focusContextKey;
      setRequestedOpen(true);
    }
  };
}

export function useStableSelectionState() {
  const selectionSnapshotRef = useRef("");
  const [selectedCellState, setSelectedCellState] = useState(null);

  const handleSelectionChange = useCallback((nextSelection) => {
    const snapshot = buildSelectionSnapshot(nextSelection);
    if (selectionSnapshotRef.current === snapshot) {
      return;
    }

    selectionSnapshotRef.current = snapshot;
    setSelectedCellState(nextSelection);
  }, []);

  return {
    handleSelectionChange,
    selectedCellState
  };
}

export function buildFocusContextKey(focusContext) {
  return JSON.stringify({
    rowIndex: focusContext?.rowIndex ?? null,
    colIndex: focusContext?.colIndex ?? null,
    rowId: focusContext?.row?.id ?? null,
    columnKey: focusContext?.column?.key ?? null,
    editing: Boolean(focusContext?.editing),
    focused: Boolean(focusContext?.focused)
  });
}

export function createSlashToolbarBindings({
  canOpen,
  focusMainArea,
  focusPrimaryControl,
  focusSecondaryControl,
  openToolbar,
  startDraftRowEdit,
}) {
  return [
    onKey("Alt+Q", () => {
      focusPrimaryControl?.();
    }, { allowInInput: true }),
    focusSecondaryControl ? onKey("Alt+E", () => {
      focusSecondaryControl();
    }, { allowInInput: true }) : null,
    onKey("Alt+N", () => {
      startDraftRowEdit?.();
    }, { allowInInput: true }),
    onKey("Alt+W", () => {
      focusMainArea?.();
    }, { allowInInput: true }),
    onKey("/", () => {
      if (!canOpen) {
        return;
      }

      openToolbar();
    })
  ].filter(Boolean);
}
